import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSessionStore } from "../../../stores/sessionStore";
import { flattenMenuSystems } from "../../../helpers/menuContract";
import { queryClient } from "../../../query/QueryProvider";
import { ADMIN_MENU_QUERY_KEY } from "../../../query/useAdminMenu";
import { isReadOnlyMode, isWriteAction } from "../../../helpers/readOnlyMode";

/**
 * RBAC 權限模型 — 客戶確認單「角色權限矩陣_260706」定案版。
 *
 * 四正式角色:
 *   Wrangler   機房/硬體管理人員(人員管理唯讀、決策分析隱藏、金額遮罩)
 *   Gatekeeper 主管/副主管(第二關審核者;可編輯;金額可見;決策分析唯讀)
 *   Viewer     一般人員(唯讀;監控/訊息/決策/審核 隱藏;金額遮罩)
 *   Overseer   老闆/大老闆(第三關主管核決;全可編輯;金額可見)
 *
 * ⚠️ 正式權限判定仍以後端回傳為準;前端遮罩僅為 UX 與防呆。
 */

export const ROLES = {
  Wrangler: { name: "機房/硬體管理員", canSeeAmount: false },
  Gatekeeper: { name: "主管/副主管", canSeeAmount: true },
  Viewer: { name: "一般使用者", canSeeAmount: false },
  Overseer: { name: "老闆/大老闆", canSeeAmount: true },
};

// 能力矩陣:'資源.動作';'*'=全部、'資源.*'=該資源全動作、'*.read'=全資源唯讀。
// 資源對應模組:user(01)/dashboard(02)/room·cabinet·hardware·software(03,05)/
//   ip·project·contract·vendor(04)/monitor(06)/notify(07)/report(08)/decision(09)/approval/import
const EDIT_MODULES = [
  "dashboard.*", "room.*", "cabinet.*", "hardware.*", "software.*",
  "ip.*", "project.*", "contract.*", "vendor.*", "report.*", "import.*",
];
const CAPABILITIES = {
  Overseer: ["*"],
  Gatekeeper: [
    // 客戶確認單:轉派等相關審核之發動＝主管階級(Gatekeeper 以上)處理
    // role = 權限設定頁,屬 01 人員管理,跟著 user 的層級走
    "user.*", "work.*", "role.*", ...EDIT_MODULES, "monitor.*", "notify.*", "approval.*", "decision.read",
  ],
  Wrangler: [
    // 人員管理唯讀 → 工作項目看得到但不能轉派、權限設定只能檢視
    "user.read", "work.read", "role.read", ...EDIT_MODULES, "monitor.*", "notify.*", "approval.*",
  ], // 人員管理唯讀、決策分析隱藏
  Viewer: [
    "user.read", "work.read", "role.read", "dashboard.read", "room.read", "cabinet.read", "hardware.read",
    "software.read", "ip.read", "project.read", "contract.read", "vendor.read", "report.read",
  ], // 全唯讀;監控/訊息/決策/審核/匯入 隱藏
};

/**
 * 權限矩陣的列與可選值（角色頁顯示用，版面依 Figma DCK-01-01 `35:3636`）。
 *
 * 客戶確認單(docs/extracted/確認單_角色權限矩陣_260706.tsv)定的是
 * **模組級三態**(可編輯/唯讀/隱藏) + **欄位級遮罩**,不是 CRUD 勾選 ——
 * 一列一個模組,值只有一個。上面的 CAPABILITIES 是同一份東西的執行版本,
 * 這裡把它換算成確認單的呈現方式,兩邊不會漂移。
 *
 * 04/05 兩列在確認單上是合併的(營運資訊、資產管理),所以一列可以對到多個資源。
 */
export const PERM_MODULES = [
  { key: "user", label: "01 人員管理", resources: ["user", "work", "role"] },
  { key: "dashboard", label: "02 智慧看板", resources: ["dashboard"] },
  { key: "room", label: "03 機房管理", resources: ["room"] },
  { key: "registry", label: "04 營運資訊（IP／專案／合約／廠商）", resources: ["ip", "project", "contract", "vendor"] },
  { key: "asset", label: "05 資產管理", resources: ["cabinet", "hardware", "software"] },
  { key: "monitor", label: "06 監控派工", resources: ["monitor"] },
  { key: "notify", label: "07 訊息功能", resources: ["notify"] },
  { key: "report", label: "08 報表類型", resources: ["report"] },
  { key: "decision", label: "09 決策分析", resources: ["decision"] },
  { key: "approval", label: "審核功能（三關簽核）", resources: ["approval"] },
  { key: "import", label: "批次匯入（各模組）", resources: ["import"] },
];

/** 模組級三態(Figma ButtonGroup 的三顆) */
export const PERM_LEVELS = [
  { key: "edit", label: "編輯" },
  { key: "read", label: "唯讀" },
  { key: "hidden", label: "隱藏" },
];

/**
 * 欄位級特殊項:關閉時該欄位於表格與表單顯示 ***（欄位保留、版型一致）。
 * 目前四項都綁在同一個「看不看得到金額」上(ROLES[].canSeeAmount)。
 */
export const PERM_FIELD_MASKS = [
  { key: "contractAmount", label: "合約管理－合約金額" },
  { key: "assetAmount", label: "資產管理－購買金額" },
  { key: "reportAmount", label: "報表功能－購買金額" },
  { key: "decisionAmount", label: "決策分析－金額數據" },
];

/**
 * 能力矩陣涵蓋的資源全集(由 PERM_MODULES 推導,不另外維護一份)。
 *
 * 存在的理由:`can("role.update")` 在 role 這個資源**根本沒被定義**時,
 * 靜態表與後端對照表都不會命中,`can()` 就靜默回 false —— 畫面看起來像
 * 「你沒有權限」,實際上是「這個資源沒有人定義過」。8/12 的權限設定頁就是
 * 這樣整頁變唯讀。這是 ADR-0004「安靜降級要吵」要防的同一類事,所以
 * 問到不認識的資源時要吵一聲並指路。
 */
const KNOWN_RESOURCES = new Set(PERM_MODULES.flatMap((m) => m.resources));

let warnedUnknownResources = new Set();
function assertKnownResource(perm) {
  const res = String(perm).split(".")[0];
  if (KNOWN_RESOURCES.has(res) || warnedUnknownResources.has(res)) return;
  warnedUnknownResources.add(res);
  console.warn(
    `[RBAC] can("${perm}") 問到的資源「${res}」不在能力矩陣裡 —— 會一律回 false(看起來像沒權限,`,
    "實際上是沒定義)。請把它加進 RbacContext 的 PERM_MODULES／CAPABILITIES,",
    "後端有對應模組時一併加進 MODULE_KEY_TO_RESOURCES。"
  );
}

/** 一組 capability pattern → `can(perm)`。三個呼叫端共用同一份判定,不各寫一次。 */
const makeCan = (caps) => (perm) => (caps || []).some((c) => matches(c, perm));

function matches(pattern, perm) {
  if (pattern === "*" || pattern === perm) return true;
  const [pRes, pAct] = pattern.split(".");
  const [res, act] = perm.split(".");
  if (pRes === "*" && pAct === act) return true;
  if (pRes === res && pAct === "*") return true;
  return false;
}

/**
 * 角色代碼 → 每個模組的三態 `{ 模組key: "edit"|"read"|"hidden" }`。
 * 一列對到多個資源時取最寬的那個(只要有一個能編輯,這一列就是可編輯)。
 * 代碼不在四個正式角色內(自訂角色)時回空物件 —— 這種角色的權限由 DB 指派,前端不知道。
 */
export function capabilityMatrix(roleCode) {
  const caps = CAPABILITIES[roleCode];
  if (!caps) return {};
  const allows = (perm) => caps.some((c) => matches(c, perm));
  return PERM_MODULES.reduce((acc, m) => {
    const canEdit = m.resources.some((r) =>
      ["create", "update", "delete"].some((a) => allows(`${r}.${a}`)));
    const canRead = m.resources.some((r) => allows(`${r}.read`));
    acc[m.key] = canEdit ? "edit" : canRead ? "read" : "hidden";
    return acc;
  }, {});
}

/** 角色代碼 → 欄位級特殊項 `{ 欄位key: boolean }`(true=顯示、false=遮罩 ***) */
export function fieldMaskMatrix(roleCode) {
  if (!CAPABILITIES[roleCode]) return {};
  const visible = !!(ROLES[roleCode] || {}).canSeeAmount;
  return PERM_FIELD_MASKS.reduce((acc, f) => ({ ...acc, [f.key]: visible }), {});
}

/** 是不是四個正式角色之一(有內建能力可以顯示) */
export const isBuiltInRole = (roleCode) => Object.keys(CAPABILITIES).includes(roleCode);

// —— 後端權限映射(ADR-0002) ——
// 後端 ModuleAccessDto.module_key ↔ 前端 capability 資源名。
// 後端 schema 未列的 module_key 安全忽略;未被涵蓋的前端資源 fallback 靜態 CAPABILITIES。
export const MODULE_KEY_TO_RESOURCES = {
  People: ["user", "work", "role"], // 01 人員管理(含權限設定／工作管理;work 無獨立後端模組)
  Dashboard: ["dashboard"], // 02 智慧看板
  Room: ["room"], // 03 機房管理
  Registry: ["ip", "project", "contract", "vendor"], // 04 營運資訊
  Asset: ["cabinet", "hardware", "software"], // 05 資產管理
  Monitor: ["monitor"], // 06 監控派工
  Notify: ["notify"], // 07 訊息功能
  Report: ["report"], // 08 報表功能
  Decision: ["decision"], // 09 決策分析
  Approval: ["approval"], // 共用:三關審核
  Import: ["import"], // 共用:批次匯入
  // —— 8/12 smoke 實測:真後端角色 modules 的三個 module_key 與前端短名不同,
  // 補 alias(同資源集)。原短名保留給 mock 種子與靜態表。
  // 表單轉換側對照 helpers/roleContract.MODULE_KEY_TO_FORM_RESOURCES(勿循環 import)。
  Operation: ["ip", "project", "contract", "vendor"], // = Registry
  Monitoring: ["monitor"], // = Monitor
  Message: ["notify"], // = Notify
};

const FIELD_KEY_AMOUNT = "ContractAmount"; // 欄位開關 ↔ 金額可見性

/** 前端認得的 access 值(ModuleAccessLevel);其餘一律換不出能力 */
const KNOWN_ACCESS = ["Edit", "View", "Hidden"];

/**
 * 「後端給了 modules 卻換不出任何能力」的診斷。
 *
 * 路由守衛上線後這個情況等於**整站鎖死**,所以訊息要能當場分辨是哪一種 ——
 * 只印 module_key 的話,三種原因看起來一模一樣:
 *   (1) module_key 對不上對照表 → 補 alias(8/12 Operation/Monitoring/Message 那型)
 *   (2) key 對得上但 access 值前端不認 → 補值對照
 *   (3) key 與值都正常、全是 Hidden → 這個角色真的沒權限,是資料不是程式
 * 講錯的後果很實際:(3) 講成「設定壞了」會讓人去找工程師,(1)(2) 講成「你沒有權限」
 * 則會讓人去跟主管要權限 —— 要了也沒用。
 */
export function diagnoseUnresolvedModules(modules) {
  const list = modules || [];
  return {
    rows: list.map((m) => `${m.module_key}=${JSON.stringify(m.access)}`),
    unknownKeys: list.filter((m) => !MODULE_KEY_TO_RESOURCES[m.module_key]).map((m) => m.module_key),
    badAccess: list
      .filter((m) => MODULE_KEY_TO_RESOURCES[m.module_key])
      .filter((m) => !KNOWN_ACCESS.includes(m.access))
      .map((m) => `${m.module_key}=${JSON.stringify(m.access)}`),
    allHidden: list.length > 0
      && list.every((m) => MODULE_KEY_TO_RESOURCES[m.module_key] && m.access === "Hidden"),
  };
}

export function unresolvedModulesMessage(modules) {
  const d = diagnoseUnresolvedModules(modules);
  return [
    "[RBAC] 後端 modules 換不出任何能力,權限將整批落空(路由守衛會擋下每一頁)。",
    `  後端實際給的:${d.rows.join(", ")}`,
    d.unknownKeys.length
      ? `  ✗ 這些 module_key 不在 MODULE_KEY_TO_RESOURCES:${d.unknownKeys.join(", ")} → 補 alias`
      : "",
    d.badAccess.length
      ? `  ✗ 這些模組的 access 值前端不認得(只認 ${KNOWN_ACCESS.join("/")}):${d.badAccess.join(", ")} → 補值對照`
      : "",
    d.allHidden
      ? "  ℹ key 與 access 都正常 —— 這個角色是真的全部 Hidden(資料問題,不是對照表)"
      : "",
    "  → 對照 src/components/DCKeeper/RbacGuard/RbacContext.js 的 MODULE_KEY_TO_RESOURCES",
  ].filter(Boolean).join("\n");
}

/**
 * GetMenu 可見的模組 → 降級能力(ADR-0002 補述一 8/14、補述二 8/16)。
 *
 * 為什麼可以這樣推:`AdminMenu/GetMenu` 的 swagger 摘要寫的是
 * 「取得後台管理左選單(**依目前登入者權限過濾**)」—— 後端在那支端點已經替這個人
 * 做過一次權限判斷。它願意把模組放進選單,就代表它同意這個人看得到該模組。
 *
 * **8/16 把降級等級從「唯讀」改成「可編輯」**,理由見 ADR-0002 補述二,摘要三點:
 *   1. 唯讀降級解不開死結 —— 要把 `modules` 填對得進「人員管理 › 權限設定」,
 *      而那一頁本身就要 `role.update`。所有人唯讀 = 沒有任何人能修好權限。
 *   2. 前端這道閘門**不是安全邊界**:smoke 實測後端連 JWT 都沒驗(不帶 token
 *      照樣回 200,見 route-guard/01 的 C1)。擋住的只有正牌使用者,擋不到任何人。
 *   3. 寫入本來就要送審 —— 角色/機房這些頁的編輯與刪除都是開審核單,不是直寫。
 * 想要保守版的,設 `REACT_APP_RBAC_MENU_FALLBACK=read` 即可退回唯讀。
 *
 * 依然是**有條件的**:角色只要有任何一個 `View`/`Edit`,這條路徑完全不會跑。
 */
const menuFallbackLevel = () =>
  process.env.REACT_APP_RBAC_MENU_FALLBACK === "read" ? "read" : "edit";

/** 側欄用的同一把 GetMenu 快取 → 目前可見的 system_name（含 alias） */
function readLiveMenuSystems() {
  const menu = queryClient.getQueryData(ADMIN_MENU_QUERY_KEY);
  return Array.isArray(menu) ? [...flattenMenuSystems(menu)] : [];
}

function sameSystemList(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function isAdminMenuEvent(event) {
  const key = event && event.query && event.query.queryKey;
  return Array.isArray(key) && key[0] === ADMIN_MENU_QUERY_KEY[0];
}

/**
 * 守衛跟側欄共用 GetMenu：快取更新時重算，不靠 session 裡可能是空的 snapshot。
 *
 * 只聽 adminMenu 這把 key，而且內容沒變就回同一個陣列 ——
 * 訂閱整份 cache 再 `setState(新陣列)` 會跟 Header／側欄的 useQuery 互踢，
 * 登入後 Suspense spinner 轉不停（內容區空白、側欄已經出來）。
 */
function useLiveMenuSystems() {
  const [systems, setSystems] = useState(readLiveMenuSystems);
  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (!isAdminMenuEvent(event)) return;
      const next = readLiveMenuSystems();
      setSystems((prev) => (sameSystemList(prev, next) ? prev : next));
    });
  }, []);
  return systems;
}

function resolveMenuSystems(backendPerms, liveMenuSystems) {
  if (liveMenuSystems && liveMenuSystems.length) return liveMenuSystems;
  return (backendPerms && backendPerms.menu_systems) || [];
}

/** 選單可見的 system_name → 前端資源集合 */
function menuResourcesFrom(systems) {
  const res = new Set();
  (systems || []).forEach((s) => {
    (MODULE_KEY_TO_RESOURCES[s] || []).forEach((r) => res.add(r));
  });
  return res;
}

function menuFallbackCapsFrom(systems) {
  const suffix = menuFallbackLevel() === "read" ? ".read" : ".*";
  return [...menuResourcesFrom(systems)].map((r) => `${r}${suffix}`);
}

let warnedMenuFallback = false;
function warnMenuFallback(backendPerms, menuSystems) {
  if (warnedMenuFallback) return;
  warnedMenuFallback = true;
  const level = menuFallbackLevel() === "read" ? "唯讀" : "可編輯";
  const mods = (backendPerms || {}).modules || [];
  const hidden = mods.filter((m) => m.access === "Hidden").length;
  // 「這個角色還沒設定過權限」與「後端資料有問題」是兩件事,訊息要講對是哪一種 ——
  // 權限是**使用者自己在權限設定頁設的**,後端不會替誰填(ADR-0002 補述三)。
  const why = mods.length === 0
    ? "這個角色的 modules 是空的"
    : hidden === mods.length
      ? `這個角色的 ${mods.length} 個模組 access 全是 Hidden`
      : "這個角色的 modules 換不出任何前端認得的能力";
  console.warn(
    `[RBAC] ${why} —— 以 GetMenu 的可見模組推導**${level}**能力(開機路徑)。\n`
    + `  選單可見:${(menuSystems || []).join(", ")}\n`
    + `  角色 modules:${mods
      .map((m) => `${m.module_key}=${JSON.stringify(m.access)}`).join(", ") || "(空)"}\n`
    + "  → 這**不是錯誤**:全新系統的角色本來就沒有權限,而設定權限的那一頁\n"
    + "    自己也需要權限,所以未設定狀態要有路進得去(ADR-0002 補述三)。\n"
    + "    在「人員管理 › 權限設定」把這個角色的模組設起來之後,這段就不會再出現。\n"
    + "  → 要退回唯讀設 REACT_APP_RBAC_MENU_FALLBACK=read"
  );
}

// ModuleAccessLevel → capability:Edit=該資源全動作、View=唯讀、Hidden=無
let warnedUnknownModuleKeys = false; // 每次載入警一次就夠,避免 re-render 洗版
function capsFromModules(modules) {
  const caps = [];
  (modules || []).forEach((m) => {
    (MODULE_KEY_TO_RESOURCES[m.module_key] || []).forEach((res) => {
      if (m.access === "Edit") caps.push(`${res}.*`);
      else if (m.access === "View") caps.push(`${res}.read`);
      // Hidden → 不加任何 capability
    });
  });
  // 「安靜降級要吵」(ADR-0004):後端有給 modules 卻換不出任何能力時,權限會整批
  // 落空 —— 路由守衛上線後這等於整站鎖死,所以訊息要能**當場分辨是哪一種**:
  //   (1) module_key 對不上對照表 → 補 alias(8/12 Operation/Monitoring/Message 那型)
  //   (2) key 對得上但 access 值前端不認 → 補值對照(前端只認 Edit/View/Hidden)
  //   (3) key 與值都正常、全是 Hidden → 這個角色真的沒有權限,是資料不是程式
  // 只印 module_key 的話,(2)(3) 看起來會跟 (1) 一模一樣。
  if ((modules || []).length > 0 && caps.length === 0 && !warnedUnknownModuleKeys) {
    warnedUnknownModuleKeys = true;
    console.warn(unresolvedModulesMessage(modules));
  }
  return caps;
}

function coveredResources(modules) {
  return new Set(
    (modules || []).flatMap((m) => MODULE_KEY_TO_RESOURCES[m.module_key] || [])
  );
}

let warnedNoProvider = false;
function warnNoProvider() {
  if (warnedNoProvider) return;
  warnedNoProvider = true;
  console.warn(
    "[RBAC] useRbac() 在 RbacProvider 之外被呼叫,權限退回",
    `${DEV_DEFAULT_ROLE} 的靜態能力。畫面會像「沒有權限」但其實是少包了 Provider。`
  );
}

const RbacContext = createContext(null);

// 無 session 時的預設角色。**一定要是權限最小的那個** ——
// 原本寫 Overseer 並註記「安全預設」,但那是最高權限,方向剛好相反:
// 權限系統掛掉時系統會變成全開。路由守衛上線後這個值直接決定降級行為
// (票 15 B4 / route-guard/01)。
const DEV_DEFAULT_ROLE = "Viewer";

// 角色來源:登入 session(單一事實來源);initialRole 供測試/預覽覆寫。
// capability 來源(ADR-0002):session 有身分組裝的後端 modules/fields 時以後端為準,
// 未涵蓋的資源與無後端資料時 fallback 靜態 CAPABILITIES(前端遮罩僅為 UX 防呆)。
export const RbacProvider = ({ children, initialRole }) => {
  const sessionUser = useSessionStore((s) => s.user);
  const setSessionRole = useSessionStore((s) => s.setRole);
  const liveMenuSystems = useLiveMenuSystems();
  const role = initialRole || (sessionUser && sessionUser.role) || DEV_DEFAULT_ROLE;
  // initialRole(測試/預覽)固定走靜態表
  const backendPerms =
    !initialRole && sessionUser && sessionUser.permissions_source === "backend"
      ? sessionUser.permissions
      : null;

  const value = useMemo(() => {
    const staticCaps = CAPABILITIES[role] || [];
    const rawBackendCaps = backendPerms ? capsFromModules(backendPerms.modules) : null;
    // 兩個後端來源打架時的降級(ADR-0002 補述,8/14):
    // `DcCustomerRole/GetById` 說全部 Hidden,但 `AdminMenu/GetMenu`(swagger 明寫
    // 「依目前登入者權限過濾」)回了整份可見選單 —— 後端自己給了兩個不同的答案。
    // 這種時候整站鎖死是最糟的選擇:使用者連「去哪裡開權限」都進不去。
    // 取捨:**選單看得到的模組給能力**(選單本身就是後端過濾過的)。
    // 8/16 起等級預設為「可編輯」—— 唯讀版解不開「要改權限得先有權限」的死結,
    // 而且前端這道閘門不是安全邊界(後端未驗 JWT)。詳見 menuFallbackCaps 的註解。
    //
    // 選單來源優先用側欄同一把 GetMenu 快取(`visible` 過濾),session.menu_systems
    // 只是組裝當下的 snapshot —— 現場 Registered 帳號那份常是空的。
    const menuSystems = resolveMenuSystems(backendPerms, liveMenuSystems);
    const menuCaps = menuFallbackCapsFrom(menuSystems);
    const usingMenuFallback = !!rawBackendCaps && rawBackendCaps.length === 0 && menuCaps.length > 0;
    if (usingMenuFallback) warnMenuFallback(backendPerms, menuSystems);
    const backendCaps = usingMenuFallback ? menuCaps : rawBackendCaps;
    const canFromBackend = makeCan(backendCaps);
    const canFromStatic = makeCan(staticCaps);
    // 「後端說了算」的資源集合。降級生效時要換成**選單**推出來的資源 ——
    // 否則 `modules: []`(GetAll 的列表投影就是空陣列)會讓 covered 是空集合,
    // 降級算出來的能力一條都問不到,等於白算。
    const covered = backendPerms
      ? (usingMenuFallback ? menuResourcesFrom(menuSystems) : coveredResources(backendPerms.modules))
      : null;
    const amountField = backendPerms
      ? (backendPerms.fields || []).find((f) => f.field_key === FIELD_KEY_AMOUNT)
      : null;
    return {
      role,
      roleName:
        (ROLES[role] || {}).name ||
        (sessionUser && sessionUser.role_name) || // 後端自訂角色
        role,
      // dev 角色切換器 → 寫回 session;測試以 initialRole 固定時為 no-op
      setRole: initialRole ? () => {} : setSessionRole,
      can: (perm) => {
        assertKnownResource(perm);
        // UAT 唯讀模式(helpers/readOnlyMode):寫入動作一律不給,不問角色也不問後端。
        // 這是全站「只留預覽」的第一道關卡 —— RbacGuard／canEdit／審核同意鍵都經過這裡。
        if (isReadOnlyMode() && isWriteAction(perm)) return false;
        // 選單降級時 covered 往往是空的(module_key 對不上／modules 空陣列),
        // 不能再問 covered.has —— 否則能力在 backendCaps 裡、can() 卻走靜態表,
        // 自訂角色就整站「沒有權限」(GetMenu 明明全開)。
        if (usingMenuFallback) return canFromBackend(perm);
        if (backendCaps && covered.has(perm.split(".")[0])) return canFromBackend(perm);
        return canFromStatic(perm);
      },
      // 「後端給了 modules,但一個都沒解析出來,而且靜態表也沒有這個角色」——
      // 那不是「這個人沒有權限」,是**對照表沒對上**(自訂角色 + 沒見過的 module_key)。
      // 路由守衛上線後這兩者的後果差很多:前者該擋,後者是整站鎖死的設定錯誤,
      // 要分得出來才講得出正確的話(8/13 code review;票 13 同型)。
      //
      // 8/14 再分一層:**全部 Hidden 也會讓能力落空,但那不是設定壞掉** ——
      // 是這個角色真的沒被開權限(實測:testenv 某角色 11 個 module_key 全部認得、
      // access 全是 Hidden)。講成「設定異常」會把人送去找工程師,但該找的是
      // 能到「權限設定」開權限的管理者。兩種都擋,但要講對是哪一種。
      hasNoResolvedPermissions:
        !!backendPerms
        && (backendPerms.modules || []).length > 0
        && (backendCaps || []).length === 0
        && staticCaps.length === 0
        && !diagnoseUnresolvedModules(backendPerms.modules).allHidden,
      allModulesHidden:
        !!backendPerms
        && (backendCaps || []).length === 0
        && diagnoseUnresolvedModules(backendPerms.modules).allHidden,
      canSeeAmount: amountField
        ? !!amountField.enabled
        : !!(ROLES[role] || {}).canSeeAmount,
    };
  }, [role, initialRole, setSessionRole, backendPerms, sessionUser, liveMenuSystems]);

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
};
RbacProvider.propTypes = {
  children: PropTypes.node,
  initialRole: PropTypes.string,
};

export function useRbac() {
  const ctx = useContext(RbacContext);
  if (!ctx) {
    // 沒包 Provider 時的預設。原本是 `can: () => true`(全開)—— 任何忘了包
    // Provider 的入口都會變成無限權限,方向錯了。改成走 Viewer 的靜態能力:
    // 讀得到、動不了,開發期不會崩潰,也不會憑空長出權限。
    const fallbackCan = makeCan(CAPABILITIES[DEV_DEFAULT_ROLE]);
    return {
      role: DEV_DEFAULT_ROLE,
      roleName: ROLES[DEV_DEFAULT_ROLE].name,
      setRole: () => {},
      can: (perm) => {
        // 降級要吵(ADR-0004)。路由守衛上線後,忘了包 Provider 的入口會整片變成
        // 「沒有權限」畫面 —— 沒有這聲警告的話,看起來會像權限設定有問題。
        warnNoProvider();
        assertKnownResource(perm);
        if (isReadOnlyMode() && isWriteAction(perm)) return false;
        return fallbackCan(perm);
      },
      canSeeAmount: !!(ROLES[DEV_DEFAULT_ROLE] || {}).canSeeAmount,
      hasNoResolvedPermissions: false,
    };
  }
  return ctx;
}
